/**
 * Data layer for `predictive-anomaly-detection`. Thin typed fetch wrappers over /api/v1/predictive-anomaly-detection.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PredictiveAnomalyDetectionRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPredictiveAnomalyDetectionResponse {
	count: number;
	items: PredictiveAnomalyDetectionRow[];
}

export interface CreatePredictiveAnomalyDetectionBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/predictive-anomaly-detection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPredictiveAnomalyDetectionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPredictiveAnomalyDetectionResponse>(res);
}

export async function create(body: CreatePredictiveAnomalyDetectionBody): Promise<PredictiveAnomalyDetectionRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PredictiveAnomalyDetectionRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const predictiveAnomalyDetectionQueryKeys = {
	all: ["predictive-anomaly-detection"] as const,
	list: () => [...predictiveAnomalyDetectionQueryKeys.all] as const,
	detail: (id: string) => [...predictiveAnomalyDetectionQueryKeys.all, id] as const,
};
